import { FunctionComponent, useState } from 'react';

import { Stack, Typography } from '@mui/material';
import { Delete, Edit, Link as LinkIcon } from '@mui/icons-material';

import { INoticeLink } from '@dw/models/notice';

import { Button } from './Button';
import { EditorListSection } from './EditorListSection';
import { HStack } from './HStack';
import { IconButton } from './IconButton';
import { LinkEditorModal } from './LinkEditorModal';

interface ILinkEditorListProps {
  value: INoticeLink[];
  onChange: (links: INoticeLink[]) => void;
  /** How many links the list will hold. Add is disabled — not hidden — once it is reached, and says why. */
  maxLinks: number;
  disabled?: boolean;
}

/** Which row the sub-modal is editing. Snapshotted on open so a re-render cannot swap it mid-edit. */
interface ILinkBeingEdited {
  index: number;
  link: INoticeLink;
}

/**
 * The links pinned alongside a notice, added and edited one at a time.
 *
 * Sibling of `AttachmentUploadList` by design — the two sit together in the notice editor, so they
 * share the `EditorListSection` shell, an Add treatment and a per-row remove. The rows differ in one
 * way that is true of the data rather than decorative: a link's human description leads and its
 * address is subordinate, because the description is the thing a family actually taps.
 */
export const LinkEditorList: FunctionComponent<ILinkEditorListProps> = (props: ILinkEditorListProps) => {

  const { value, onChange, maxLinks, disabled } = props;

  const [editorOpen, setEditorOpen] = useState(false);
  const [linkBeingEdited, setLinkBeingEdited] = useState<ILinkBeingEdited>();

  const atMaxLinks = value.length >= maxLinks;

  const handleAdd = () => {
    setLinkBeingEdited(undefined);
    setEditorOpen(true);
  };

  const handleEdit = (index: number, link: INoticeLink) => {
    setLinkBeingEdited({ index, link });
    setEditorOpen(true);
  };

  const handleSave = (link: INoticeLink) => {
    onChange(linkBeingEdited
      ? value.map((x, i) => i === linkBeingEdited.index ? link : x)
      : [...value, link]);

    setEditorOpen(false);
  };

  const handleRemove = (index: number) => onChange(value.filter((_, i) => i !== index));

  return (
    <>
      <EditorListSection
        title='Links'
        caption={`Up to ${maxLinks} links`}
        action={(
          <Button
            startIcon={<LinkIcon />}
            size='small'
            variant='text'
            type='button'
            onClick={handleAdd}
            disabled={disabled || atMaxLinks}
            tooltip={atMaxLinks ? `You can add up to ${maxLinks} links` : undefined}
          >
            Add Link
          </Button>
        )}
      >
        {value.length > 0 && (
          <Stack rowGap={1}>
            {value.map((link, index) => (
              <HStack key={index} breakpoint={false} columnGap={1} justifyContent='space-between'>
                {/* minWidth={0} lets the flex child shrink so `noWrap` can ellipsis a 500-character
                    address instead of pushing the row actions off the modal. */}
                <HStack breakpoint={false} columnGap={1} flex={1} minWidth={0}>
                  <LinkIcon fontSize='small' color='disabled' />

                  <Stack minWidth={0}>
                    <Typography variant='body2' noWrap>{link.description}</Typography>
                    <Typography variant='caption' noWrap>{link.url}</Typography>
                  </Stack>
                </HStack>

                <HStack breakpoint={false} columnGap={0}>
                  <IconButton
                    icon={<Edit fontSize='small' />}
                    size='small'
                    tooltip='Edit Link'
                    onClick={() => handleEdit(index, link)}
                    disabled={disabled}
                  />

                  <IconButton
                    icon={<Delete fontSize='small' />}
                    color='error'
                    size='small'
                    tooltip='Remove Link'
                    onClick={() => handleRemove(index)}
                    disabled={disabled}
                  />
                </HStack>
              </HStack>
            ))}
          </Stack>
        )}
      </EditorListSection>

      <LinkEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        link={linkBeingEdited?.link}
        onSave={handleSave}
      />
    </>
  );
};
